import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sign in · ClutchCube" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const router = useRouter();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin/investor-leads` },
        });
        if (error) throw error;
        setNotice("Account created. You can sign in now.");
        setMode("signin");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        router.navigate({ to: "/admin/investor-leads" });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-arena flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-card border border-border rounded-sm p-8">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-2">ClutchCube · Admin</p>
        <h1 className="font-display text-3xl mb-6">{mode === "signin" ? "Sign in" : "Create account"}</h1>

        <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-background border border-border rounded-sm px-4 py-3 mb-4"
        />
        <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Password</label>
        <input
          type="password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-background border border-border rounded-sm px-4 py-3 mb-4"
        />

        {error && <p className="text-xs text-destructive mb-3">{error}</p>}
        {notice && <p className="text-xs text-primary mb-3">{notice}</p>}

        <button
          type="submit"
          disabled={busy}
          className="w-full px-6 py-3 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm disabled:opacity-60"
        >
          {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
        </button>

        <button
          type="button"
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          className="mt-4 w-full text-xs uppercase tracking-[0.2em] text-muted-foreground"
        >
          {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
        </button>
      </form>
    </div>
  );
}
